import {
  Avatar as ChatUiAvatar, ChatContainer,
  ConversationHeader, InputToolbox, Message as ChatUiMessage, MessageInput, MessageList, TypingIndicator
} from "@chatscope/chat-ui-kit-react";
import { Badge, Box, IconButton, Link, Typography, useMediaQuery, Chip, Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { AccountCircleOutlined, ArrowUpwardRounded, FilterList, Group, GetApp, ExitToApp, Warning, ThumbUp, Edit, Save, Cancel, Star } from "@material-ui/icons";
import { PushPin } from './pushPinIcons';
import { Sparkle } from './sparkleIcon';
import { ToggleButton } from "@material-ui/lab";
import { Button, HtmlMessageContent, Tooltip, ConfirmationButton, ConfirmDialog, CircularLoader, TextField, BorrowerIndicatorIcon } from "@roc/ui";
import { Message, Participant } from "@twilio/conversations";
import clsx from "clsx";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CommunicationStore } from "../stores";
import { AI_SUMMARY_CHATROOM_TYPES_ALLOWED, ChatRoomType, ChatRoomStatus, ConversationAttribute, EscalatableTask, ParticipantAttribute, ParticipantDetails, MessageAttribute, SupportedEmojis } from "../types/communicationTypes";
import { filterByTagsSelected, getAuthorName, getRoleInfoForAuthor, getIcon, getMessageTime, isInternal, cleanHtmlText, getUserFromAllowedParticipants, checkIfExistingRating } from "./../helper";
import { AttachmentPreview } from "./attachments/attachmentPreview";
import { CustomMessageInputContainer } from "./customMessageInput";
import { FilterTagsContainer } from "./filterTagsContainer";
import { MessageHoverMenu } from "./hoverMenu";
import { JoinToolBox } from "./joinToolBox";
import { ManageParticipantsDialog } from "./manageParticipantsDialog";
import { ReplyButton } from "./replyButton";
import { ReplyToMessage } from "./replyToMessage";
import { TechSupportCommunicationStore } from "../stores/techSupportCommunicationStore";
import { ArchivedChatInfoBox } from "./archivedChatInfoBox";
import { useUserStore } from "@roc/feature-app-core";
import { isNil } from "@roc/feature-utils";
import { DisplayNameWithOnlineStatus } from "@roc/feature-app-core";
import { ReadReceiptButton } from "./readReceiptButton";
import Rating from '@material-ui/lab/Rating';
import { CreateTaskDialog } from 'libs/feature-general-tasks/src/generalTasks/components/editTask/createTaskDialog';
import { useGeneralTasksStore } from '@roc/feature-general-tasks';
import { BestPractices } from "./bestPractices";
import { ConversationSummary } from "./conversationSummary";
import { PinnedMessages } from "./pinnedMessages";
import Drawer from '@material-ui/core/Drawer';
import { LikesChip } from "./likesChip";
import { FullReadOnlyChatInfoBox } from "./fullReadOnlyChatInfoBox";
import { ReplyInTaskButton } from "./replyInTask/replyInTaskButton";
import { ReplyInTaskDialog } from "./replyInTask/replyInTaskDialog";
import { useReplyInTaskStore } from "../hooks";

const SLIM_HEADER_HEIGHT = 50;

const useStyles = makeStyles(theme => ({
  conversationAvatar: {
    background: '#EEE',
    padding: 4,
  },
  slimHeader: {
    '& .cs-conversation-header': {
      padding: 0,
      alignItems: 'center',
      height: SLIM_HEADER_HEIGHT,
      '& .cs-conversation-header__avatar': {
        margin: '0 8px'
      },
      '& .search-container': {
        top: SLIM_HEADER_HEIGHT
      }
    }
  },
  conversationHeader: {
    position: 'relative',
    '& .search-container': {
      position: 'absolute',
      left: '0',
      top: 68,
      minHeight: 60,
      width: '100%',
      padding: 8,
      background: '#fff',
      borderBottom: '1px solid #ccc'
    }
  },
  messageList: {
    position: 'relative',
    '& .scrollbar-container': {
      padding: 0,
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      overflowY: 'auto !important',
      '& >div:first-child': {
        paddingBottom: 24,
        marginBottom: '0px !important',
      },
      '& .ps__rail-y': {
        display: 'none',
      },
    },
    [theme.breakpoints.down(769)]: {
      '& .scrollbar-container': {
        padding: 0,
        overflow: 'auto !important',
        '& .ps__rail-y': {
          display: 'none',
        },
      },
    }
  },
  message: {
    padding: '16px 16px 0 16px',
    '& .cs-message__content-wrapper': {
      position: 'relative',
    },
    '& .cs-message__avatar': {
      justifyContent: 'flex-start',
    },
    '& .cs-message__content': {
      padding: 0,
      borderRadius: '8px !important',
      '& ul': {
        paddingLeft: 24,
      }
    },
    '&.cs-message--outgoing': {
      marginLeft: '100px !important',
    },
    '&.cs-message--incoming': {
      marginRight: '100px !important',
    },
    '&.cs-message--outgoing .cs-message__content': {
      color: '#f1f1f1',
      backgroundColor: '#333',
    },
    '&.cs-message--incoming .cs-message__content': {
      color: 'black',
      backgroundColor: '#eee',
    },
    '& .cs-message__footer': {
      alignItems: 'flex-end',
      '& .cs-message__sent-time': {
        fontSize: 12,
        paddingLeft: 80
      },
    },
    '& .wysiwyg-mention': {
      color: '#ffffff',
      fontWeight: '700'
    },
  },
  loadingContentMessageList: {
    position: 'absolute',
    marginTop: 24,
    width: '100%',
    height: '100%',
    textAlign: 'center',
  },
  emptyContentMessageList: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    fontSize: '24px',
    letterSpacing: '0.5px',
    fontWeight: 'bold',
    color: '#bbb',
  },
  huddleTooltipPopper: {
    '& .MuiTooltip-tooltip': {
      fontSize: 16,
      padding: 8
    }
  },
  bestPracticesToggle: {
    '&.MuiToggleButton-root': {
      color: '#000000',
    },
  },
  pinnedMessage: {
    '& .cs-message__content': {
      outlineStyle: 'solid',
      outlineWidth: 3,
      outlineColor: '#2684ff',
    },
  },
  '@keyframes pinBlink': {
    '0%, 100%': { outlineColor: 'transparent' },
    '16.66%, 50%, 83.33%': { outlineColor: '#2684ff' },
    '33.33%, 66.66%': { outlineColor: 'transparent' },
  },
  pinHighlight: {
    outlineStyle: 'solid',
    outlineWidth: 3,
    animation: '$pinBlink 1s ease-in-out 1',
  },
  escalatedIcon: {
    color: 'rgb(217, 68, 82)',
  },
  escalatedIconButton: {
    border: '2px solid rgb(217, 68, 82)',
    borderRadius: '50%',
    padding: '4px',
  },
}
));

const useDialogContentStyles = makeStyles(() => ({
  root: {
    '& .dialog-content-box': {
      display: 'flex',
      alignItems: 'center',
    },
  }
}));

export const ChatSection = observer(({
  as,
  showLoading,
  communicationStore,
  showSnippet = false,
  allowJoinConversation = false,
  allowLeaveConversation = false,
  onBackClick,
  showEmailAddress,
  allowManageParticipants,
  allowAddInternalParticipants,
  allowAddParticipants,
  allowRemoveParticipants,
  onDownloadConversation,
  slimChatHeader = true,
  onHuddle,
  onUserNewConversation,
  onOpenAddTask,
  containerClass,
  renderReplyInTask = true,
}: {
  as: any,
  showLoading: boolean,
  communicationStore: CommunicationStore,
  showSnippet: boolean,
  allowJoinConversation: boolean,
  allowLeaveConversation: boolean,
  onBackClick?: () => void,
  showEmailAddress?: boolean,
  allowManageParticipants?: boolean,
  allowAddInternalParticipants?: boolean,
  allowAddParticipants?: boolean,
  allowRemoveParticipants?: boolean,
  onDownloadConversation?: (...any) => any,
  onUnarchiveConversation?: (...any) => any,
  slimChatHeader?: boolean,
  onHuddle?: (inviteUserIds: number[]) => void,
  onUserNewConversation?: (inviteUserId: number) => void,
  onOpenAddTask?: (userId: number) => void,
  containerClass?: string,
  renderReplyInTask?: boolean,
}) => {
  const classes = useStyles();
  const dialogContentclasses = useDialogContentStyles();
  const msgListRef = useRef(undefined);
  const [expandMessageInputArea, setExpandMessageInputArea] = React.useState<boolean>(false);
  const [selected, setSelected] = useState(false);
  const theme = useTheme();
  const isTabletSize = useMediaQuery(theme.breakpoints.down(769));
  const [bestPractices, setBestPractices] = useState(false);
  const [showAiSummary, setShowAiSummary] = useState(false);
  const [showPinnedMessages, setShowPinnedMessages] = useState(false);
  const [escalatableTask, setEscalatableTask] = useState<EscalatableTask | null>(null);
  const [showEscalateTaskModal, setShowEscalateTaskModal] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message>(null);
  const [loanId, setLoanId] = useState<number>();
  const [taskId, setTaskId] = useState<number>();
  const [chatRoomType, setChatRoomType] = useState<ChatRoomType>();
  const [companyId, setCompanyId] = useState<number>();
  const [userId, setUserId] = useState<number>();
  const [mentionsList, setMentionsList] = useState([]);
  const [uiUnreadMessagesCount, setUiUnreadMessagesCount] = useState(0);
  const [uiUnreadMessagesCountClearIntervalId, setUiUnreadMessagesCountClearIntervalId] = useState<any>();
  const [scrollToValue, setScrollToValue] = useState<number>();
  const { userStore } = useUserStore();
  const {
    showNoConversationsMessage,
    conversations,
    participants,
    messages,
    currentConversationMessagesGroupedByDate,
    twilioReplyMessages,
    messagesPaginator,
    currentConversationSid,
    typingMessage,
    currentConversationTags,
    currentConversationTagsSelected,
    manageParticipantsStore,
    currentConversationReadOnly,
    currentConversationIsArchived,
    unreadMessagesCount,
    currentChatRoomId,
    loadingCurrentConversation,
    currentConversationFullReadOnly,
  } = communicationStore;
  const { userIdParticipantDetailsMap, userIdCalendlyLinkMap, internalParticipants } = manageParticipantsStore;
  const sidRef = useRef("");
  sidRef.current = currentConversationSid;
  const ratings = userStore.ratings;
  const { replyInTaskStore } = useReplyInTaskStore();
  const { generalTasksStore } = useGeneralTasksStore();

  interface Attributes {
    firstName: string;
    lastName: string;
    userId: number;
  }

  const isInternalUser = useMemo(() => { return userStore.userInformation?.isInternal; }, [userStore.userInformation]);
  const isLoanChatApp = communicationStore.globalStore.isLoanChatApp;
  const isNportalInternal = communicationStore.globalStore.isInternalPortal;
  const isAiSummaryEnabled = isNportalInternal || isLoanChatApp;
  const isAiSummaryChatRoom = chatRoomType && AI_SUMMARY_CHATROOM_TYPES_ALLOWED.includes(chatRoomType);
  const sidePanelOpen = bestPractices || showAiSummary || showPinnedMessages;
  const chatContentWidth = isNportalInternal ? '60%' : '70%';
  const sidePanelWidth = isNportalInternal ? '40%' : '30%';

  const toggleBestPractices = () => {
    setBestPractices((prev) => {
      const next = !prev;
      if (next) {
        setShowAiSummary(false);
        setShowPinnedMessages(false);
      }
      return next;
    });
  };

  const toggleAiSummary = () => {
    setShowAiSummary((prev) => {
      const next = !prev;
      if (next) {
        setBestPractices(false);
        setShowPinnedMessages(false);
      }
      return next;
    });
  };

  const togglePinnedMessages = () => {
    setShowPinnedMessages((prev) => {
      const next = !prev;
      if (next) {
        setBestPractices(false);
        setShowAiSummary(false);
      }
      return next;
    });
  };

  const escalateTask = () => {
    setShowEscalateTaskModal(false);
    if (escalatableTask) {
      generalTasksStore.escalateTask(escalatableTask.taskId);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchEscalatableTask = async () => {
      setEscalatableTask(null);
      if (!currentChatRoomId) {
        return;
      }
      try {
        const response = await communicationStore.communicationService.getEscalatableTaskByChatRoomId(currentChatRoomId);
        if (!cancelled) {
          setEscalatableTask(response?.data?.data ?? null);
        }
      } catch (e) {
        console.error('Error fetching escalatable task:', e);
      }
    };

    fetchEscalatableTask();
    return () => { cancelled = true; };
  }, [currentChatRoomId]);

  useEffect(() => {
    if (!showAiSummary || !isAiSummaryEnabled || !isAiSummaryChatRoom || !loanId || !currentConversationSid) {
      return;
    }
    communicationStore.fetchConversationSummary(loanId, currentConversationSid);
  }, [showAiSummary, isAiSummaryEnabled, isAiSummaryChatRoom, loanId, currentConversationSid]);

  useEffect(() => {
    if (!isAiSummaryChatRoom) {
      setShowAiSummary(false);
      communicationStore.clearConversationSummary();
      return;
    }
    if (!showAiSummary) {
      communicationStore.clearConversationSummary();
    }
  }, [currentConversationSid, showAiSummary, isAiSummaryChatRoom, chatRoomType]);

  useEffect(() => {
    if (!isAiSummaryChatRoom && showAiSummary) {
      setShowAiSummary(false);
      communicationStore.clearConversationSummary();
    }
  }, [isAiSummaryChatRoom, showAiSummary]);

  useEffect(() => {
    if (messages[currentConversationSid]?.length && !loadingCurrentConversation) {
      scrollToPosition(scrollToValue);
    }
  }, [messages[currentConversationSid]?.length, loadingCurrentConversation])

  useEffect(() => {
    if (currentChatRoomId && currentConversationSid) {
      communicationStore.loadPinnedMessages(currentChatRoomId, currentConversationSid);
    }
  }, [currentChatRoomId, currentConversationSid]);

  const openedConversation = useMemo(() => {
    return conversations[currentConversationSid];
  }, [currentConversationSid, conversations[currentConversationSid]]);

  const hasParticipantsFetched = useMemo(() => {
    return participants[currentConversationSid] != undefined;
  }, [currentConversationSid, participants[currentConversationSid]]);

  const isConversationOpen = Boolean(openedConversation);

  const noMessages = useMemo(
    () => messages[currentConversationSid]?.length == 0,
    [currentConversationSid, conversations[currentConversationSid], messages[currentConversationSid]]
  );

  useEffect(() => {
    return () => {
      communicationStore.clearCurrentConversation();
    }
  }, []);

  useEffect(() => {
    if (openedConversation && !currentConversationReadOnly && !currentConversationFullReadOnly) {
      setUiUnreadMessagesCount(unreadMessagesCount[currentConversationSid]);
      clearTimeout(uiUnreadMessagesCountClearIntervalId);
      const timeoutId = setTimeout(() => {
        setUiUnreadMessagesCount(0);
      }, 60 * 1000);
      setUiUnreadMessagesCountClearIntervalId(timeoutId);
    }
  }, [openedConversation]);

  useEffect(() => {
    if (currentConversationFullReadOnly && openedConversation) {
      const conversationAttributes = openedConversation.attributes as ConversationAttribute;
      const loanId = conversationAttributes?.loanId;
      communicationStore.resetUnreadCount(loanId);
    }
    if (openedConversation && (currentConversationReadOnly || currentConversationFullReadOnly)) {
      const conversationAttributes = openedConversation.attributes as ConversationAttribute;
      const loanId = conversationAttributes?.loanId;
      setTaskId(conversationAttributes?.taskId);
      fetchParticipants(loanId, currentChatRoomId, conversationAttributes?.taskId);
    }
    if (openedConversation && !currentConversationReadOnly && !currentConversationFullReadOnly) {
      setScrollToValue(undefined);
      setReplyToMessage(null);
      const conversationAttributes = openedConversation.attributes as ConversationAttribute;
      const companyId = conversationAttributes?.companyId ? conversationAttributes?.companyId : userStore?.userInformation?.companyId ?? userStore?.globalStore?.selectedCompanyId;
      const loanId = conversationAttributes?.loanId;
      const proposalTopicId = conversationAttributes?.proposalTopicId;
      setLoanId(loanId);
      setTaskId(conversationAttributes?.taskId);
      setChatRoomType(conversationAttributes?.chatRoomType);
      setCompanyId(companyId)
      if (communicationStore.isSupportConversation) {
        communicationStore.resetUnreadCount(companyId);
      } else if (communicationStore.isProposalTopicConversation) {
        communicationStore.resetUnreadCount(proposalTopicId);
      } else if (communicationStore.isTaskConversation) {
        communicationStore.setTaskId(conversationAttributes.taskId)
        communicationStore.resetUnreadCount(loanId);
      } else {
        communicationStore.resetUnreadCount(loanId);
      }
      setUserId(conversationAttributes?.userId)
      communicationStore.removeMentionMarker(currentConversationSid);
      communicationStore.setConversationMessageDefaultTag(conversationAttributes, {
        conversationSid: openedConversation.sid,
        chatRoomType: conversationAttributes.chatRoomType
      });
      fetchParticipants(loanId, currentChatRoomId, openedConversation?.attributes.taskId);
      fetchExistingRating();
    }
  }, [openedConversation]);

  async function fetchParticipants(loanId, currentChatRoomId, chatTaskId?: number) {
    const mentionsLists = [];

    if (isNil(loanId)) {
      await manageParticipantsStore.getParticipantsAllowedByChatRoomId(currentChatRoomId, true);
    } else {
      await manageParticipantsStore.getParticipantsAllowedByLoanId(loanId, true, chatTaskId);
    }

    let participants = communicationStore.manageParticipantsStore.internalParticipants;
    let hereParticipant = {
      firstName: 'here',
      lastName: '',
      assignedToLoan: null,
      roleDisplayName: null,
      roleDisplayColor: null
    }
    participants.push(hereParticipant);
    for (const key in participants) {
      const p = participants[key];
      mentionsLists.push({
        text: (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '180px auto',
            justifyItems: 'start',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'space-between',
          }}>
            <span style={{
              display: 'flex',
              alignItems: 'center',
            }}>
              {p.roleDisplayName != null && p.assignedToLoan &&
                <Star style={{
                  fontSize: 16,
                  marginRight: 4,
                  color: '#FF9800',
                }} />
              }
              <span>{`${p.firstName?.trim()} ${p.lastName?.charAt(0)} `}</span>
            </span>
            {p.roleDisplayName != null &&
              <Chip
                variant="default"
                size="small"
                label={p.assignedToLoan ? p.roleDisplayName + " - Assigned" : p.roleDisplayName}
                style={{
                  height: 18,
                  backgroundColor: p.assignedToLoan ? '#4CAF50' : p.roleDisplayColor,
                  color: p.roleDisplayColor ? theme.palette.getContrastText(p.roleDisplayColor) : 'black',
                  fontWeight: 'bold'
                }}
              />
            }
          </div>
        ),
        value: p.assignedToLoan ? `${p.firstName?.trim()} ${p.lastName?.charAt(0)} | ${p.roleDisplayName}` : `${p.firstName?.trim()} ${p.lastName?.charAt(0)}`,
        hasRole: p.roleDisplayName != null,
        role: p.roleDisplayName || "",
        name: `${p.firstName?.trim()} ${p.lastName?.charAt(0)}`,
        assignedToLoan: p.assignedToLoan,
        url: "javascript:void(0)"
      });
    }
    mentionsLists.sort((a, b) => {
      if (a.assignedToLoan !== b.assignedToLoan) {
        return a.assignedToLoan ? -1 : 1;
      } else {
        return a.name.localeCompare(b.name);
      }
    });
    setMentionsList(mentionsLists);
  }

  useEffect(() => {
    if (currentChatRoomId != undefined) {
      manageParticipantsStore.getParticipantsDetails(currentChatRoomId, true);
    }
  }, [currentChatRoomId]);

  const getParticipantsText = () => {
    if (!participants[currentConversationSid]) {
      return `Loading...`;
    }
    else if (participants[currentConversationSid].length) {
      const text = `${participants[currentConversationSid].length} ${participants[currentConversationSid].length > 1 ? " participants" : " participant"}`;
      return (
        (allowManageParticipants && !currentConversationReadOnly && !currentConversationFullReadOnly) ?
          <Box component={'span'}>
            <Link href="#" onClick={(event: React.SyntheticEvent) => {
              event.preventDefault();
              manageParticipantsStore.openDialog();
            }} style={{
              color: theme.palette.secondary.dark
            }}>
              {text}
            </Link>
          </Box> : text
      )
    } else {
      return `No Participants`;
    }
  }

  const createMentionsArr = (mentions) => {
    let participants = communicationStore.participants;
    const keys = participants[currentConversationSid] && Object.keys(participants[currentConversationSid]);
    let mentionsArr = [];
    let uniqueMentions = [...new Set(mentions)];
    if (!uniqueMentions.includes("here ")) {
      for (let i in uniqueMentions) {
        let plainNameMention = (uniqueMentions[i] as string).split('|')[0].trim();
        for (let participant in keys) {
          const p = participants[currentConversationSid][participant].attributes as Attributes;
          const name = p.firstName + " " + p.lastName?.charAt(0);
          if (name === plainNameMention) {
            mentionsArr.push(participants[currentConversationSid][participant].sid);
          }
        }
      }
    } else {
      const loggedInUser = communicationStore.getloggedInParticpant(communicationStore.participants[communicationStore.currentConversationSid])?.attributes as ParticipantAttribute;
      const loggedInUserName = loggedInUser.firstName + " " + loggedInUser.lastName?.charAt(0);
      for (let participant in keys) {
        const p = participants[currentConversationSid][participant].attributes as Attributes;
        const name = p.firstName + " " + p.lastName?.charAt(0);
        if (name !== loggedInUserName) {
          mentionsArr.push(participants[currentConversationSid][participant].sid);
        }
      }
    }
    return mentionsArr
  }

  const onMessageSend = async (message, attachments, mentions) => {
    let mentionsArr = createMentionsArr(mentions);
    communicationStore.sendMessage(message, attachments, replyToMessage, mentionsArr, () => {
      setUiUnreadMessagesCount(0);
      setReplyToMessage(null);
      setScrollToValue(undefined);
    });
  };

  const toggleExpand = () => {
    setExpandMessageInputArea(!expandMessageInputArea);
  };

  const scrollToPosition = (scrollToValue = undefined) => {
    setTimeout(() => {
      const messagesContainerEl: any = document.getElementById(`message-list-container-${currentConversationSid}`);
      let scrollTop = messagesContainerEl.scrollHeight;
      if (scrollToValue) {
        scrollTop = Math.max(0, (messagesContainerEl.scrollHeight - scrollToValue));
      } else {
        // simply scroll to bottom
        scrollTop = messagesContainerEl.scrollHeight;
      }
      messagesContainerEl.scrollTo({
        behavior: 'instant',
        top: scrollTop
      });
    }, 100);
  };

  const isChatRoomActive = () => {
    return communicationStore.conversationChatRoomStatus[currentConversationSid] !== ChatRoomStatus.ARCHIVED;
  }

  const fetchExistingRating = async () => {
    try {
      const loggedInUser = communicationStore.getloggedInParticpant(communicationStore.participants[communicationStore.currentConversationSid])?.attributes as ParticipantAttribute;
      await userStore.fetchAllRatingsByChatRoomIdAndUserId(currentChatRoomId, loggedInUser.userId);
    } catch (error) {
      console.error('Error fetching existing ratings for this chat:', error);
    }
  };

  const unreadMessageStartSid = useMemo(
    () => {
      if (messages[currentConversationSid]?.length && uiUnreadMessagesCount > 0) {
        const unreadMessageStartIndex = (Math.max(messages[currentConversationSid].length - uiUnreadMessagesCount, 0));
        return messages[currentConversationSid][unreadMessageStartIndex].sid;
      }
      return null;
    },
    [uiUnreadMessagesCount, messages[currentConversationSid]]
  );

  const scrollAndHighlightMessage = (messagesContainerEl, replyMessageId) => {
    const replyMessageEl: any = messagesContainerEl.querySelector(`#container-${replyMessageId}`);
    if (replyMessageEl) {
      const replyMessageContentEl: any = replyMessageEl.querySelector(".cs-message__content");
      messagesContainerEl.scrollTo({
        behavior: 'smooth',
        top: replyMessageEl.offsetTop
      });
      replyMessageContentEl.classList.remove(classes.pinHighlight);
      void replyMessageContentEl.offsetWidth;
      replyMessageContentEl.classList.add(classes.pinHighlight);
      setTimeout(() => {
        replyMessageContentEl.classList.remove(classes.pinHighlight);
      }, 1000);
    }
  };

  const handleOpenReplyInTaskModal = (replyToMessage) => {
    replyInTaskStore.setSelectedMessage(replyToMessage);
    replyInTaskStore.setLoanId(loanId);
    replyInTaskStore.setModalOpen(true);
    replyInTaskStore.setReadOnly(false);
  }

  const handleClickHereToOpenReplyInTaskModal = (taskIdParam) => {
    replyInTaskStore.setTaskId(taskIdParam);
    replyInTaskStore.setLoanId(loanId);
    replyInTaskStore.setModalOpen(true);
    replyInTaskStore.setReadOnly(true);
  }

  const sendReplyInTaskMessage = (message: string, taskIdParam: number, replyToMessage: Message) => {
    communicationStore.sendMessage(message, [], replyToMessage, [], () => { }, { taskId: taskIdParam });
  }

  const isUWChatRoom = chatRoomType === ChatRoomType.UNDERWRITING;
  const canEscalateTask = isNil(escalatableTask?.escalated) && !generalTasksStore.isTaskEscalated(escalatableTask?.taskId);

  return (
    <>
      <CreateTaskDialog loanId={loanId} taskId={taskId} />
      {renderReplyInTask && replyInTaskStore.loanId && replyInTaskStore.isModalOpen && (
        <ReplyInTaskDialog replyInTaskStore={replyInTaskStore} sendReplyInTaskMessage={sendReplyInTaskMessage} />
      )}
      <ChatContainer className={clsx(slimChatHeader ? classes.slimHeader : null, containerClass)}>
        {
          isConversationOpen ?
            <ConversationHeader className={classes.conversationHeader}>
              {
                onBackClick && <ConversationHeader.Back style={{
                  display: isTabletSize ? 'inherit' : 'none'
                }} onClick={onBackClick} />
              }
              <ChatUiAvatar src={getIcon(openedConversation)} className={classes.conversationAvatar} />
              <ConversationHeader.Content>
                <CustomConversationHeaderContent
                  loanId={loanId}
                  chatRoomType={chatRoomType}
                  displayName={communicationStore.getConversationDisplayName(openedConversation) ?? currentConversationSid}
                  communicationStore={communicationStore}
                  hasParticipantsFetched={hasParticipantsFetched}
                />
                <div className="cs-conversation-header__info">{getParticipantsText()}</div>
                {
                  selected && currentConversationTags.length > 0 && <div className="search-container">
                    <FilterTagsContainer
                      data={currentConversationTags}
                      selectedData={currentConversationTagsSelected}
                      onChange={(newValues) => {
                        communicationStore.setCurrentConversationTagsSelected(newValues);
                      }}
                    />
                  </div>
                }
              </ConversationHeader.Content>
              <ConversationHeader.Actions>
                {isInternalUser && <ToggleButton
                  style={{
                    border: 'none',
                  }}
                  className={classes.bestPracticesToggle}
                  value="check"
                  selected={bestPractices}
                  onChange={toggleBestPractices}>
                  <Box height={'100%'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                    <ThumbUp fontSize="small" style={{ marginRight: '2px' }} />
                    <Typography style={{ fontSize: '14px', margin: 'auto 3px' }} display="inline">Best</Typography>
                    <Typography style={{ fontSize: '14px' }} display="inline">Practices</Typography>
                  </Box>
                </ToggleButton>}
                {isConversationOpen && isChatRoomActive() && !communicationStore.isSupportConversation && !currentConversationReadOnly && allowLeaveConversation && !currentConversationFullReadOnly &&
                  <ConfirmationButton style={{ backgroundColor: 'transparent' }}
                    testId="leave"
                    fullWidth
                    onClick={() => manageParticipantsStore.leaveConversation(participants, currentConversationSid)}
                    endIcon={<ExitToApp />}
                    title="Leave"
                    confirmDialogProps={{
                      title: `Leave ${communicationStore.getConversationDisplayName(openedConversation) ?? currentConversationSid} conversation`,
                      dialogContentclasses: dialogContentclasses,
                      body: (
                        <>
                          <Warning
                            color="error"
                            fontSize="large"
                            style={{ marginRight: '16px' }}
                          />
                          <span>
                            <strong>{`Are you sure you want to leave the ${communicationStore.getConversationDisplayName(openedConversation) ?? currentConversationSid} conversation?`}</strong>{`  You can join this conversation again at any time.`}
                          </span>
                        </>
                      ),
                      confirmButtonText: 'Leave',
                    }}
                  >
                    Leave
                  </ConfirmationButton>
                }
                {
                  currentConversationTags.length > 0 &&
                  <Tooltip title="Conversation Tags" placement="top" arrow>
                    <ToggleButton
                      style={{
                        border: 'none'
                      }}
                      value="check"
                      selected={selected}
                      onChange={() => {
                        setSelected(!selected);
                      }}>
                      <Badge color="error" variant="dot" invisible={currentConversationTagsSelected?.length == 0}>
                        <FilterList />
                      </Badge>
                    </ToggleButton>
                  </Tooltip>
                }
                {isChatRoomActive() && allowManageParticipants && !currentConversationReadOnly && !currentConversationFullReadOnly && (
                  <Tooltip title="Manage Participants" placement="top" arrow>
                    <IconButton data-testid="manage-participants" onClick={() => manageParticipantsStore.openDialog()}>
                      <Group fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isChatRoomActive() && onDownloadConversation && (
                  <Tooltip title="Download Conversation" placement="top" arrow>
                    <IconButton data-testid="download-conversation" onClick={onDownloadConversation}>
                      <GetApp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isAiSummaryEnabled && isAiSummaryChatRoom && (
                  <Tooltip title="AI Summary" placement="top" arrow>
                    <IconButton data-testid="ai-summary" onClick={toggleAiSummary}>
                      <Sparkle fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {isChatRoomActive() && escalatableTask && !currentConversationFullReadOnly && (
                  <Tooltip title={canEscalateTask ? "Escalate Task" : "Escalated"} placement="top" arrow>
                    <span>
                      <IconButton
                        data-testid="escalate-task"
                        disabled={!canEscalateTask}
                        onClick={() => setShowEscalateTaskModal(true)}
                        className={canEscalateTask ? undefined : classes.escalatedIconButton}
                      >
                        <ArrowUpwardRounded fontSize="small" className={canEscalateTask ? undefined : classes.escalatedIcon} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {isChatRoomActive() && (
                  <Tooltip title="Pinned Messages" placement="top" arrow>
                    <IconButton data-testid="pinned-messages" onClick={togglePinnedMessages}>
                      <PushPin fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ConversationHeader.Actions>
            </ConversationHeader> :
            <ConversationHeader className={classes.conversationHeader} style={{
              display: 'none'
            }}>
              <ConversationHeader.Content />
            </ConversationHeader>
        }
        {
          expandMessageInputArea ? null :
            <MessageList
              className={classes.messageList}
              style={{
                marginTop: (selected && currentConversationTags.length > 0) ? 60 : 0
              }}
              autoScrollToBottom={false}
              ref={msgListRef}
              typingIndicator={typingMessage ? <TypingIndicator content={typingMessage} /> : undefined}>
              {
                loadingCurrentConversation ?
                  <MessageList.Content className={classes.loadingContentMessageList}>
                    <CircularLoader position="absolute" thickness={5} color="primary" size={36} />
                  </MessageList.Content> : null
              }
              {
                isConversationOpen && !loadingCurrentConversation && hasParticipantsFetched && messages[currentConversationSid] &&
                <MessageList.Content style={{
                  width: '100%',
                  marginBottom: 24,
                  display: 'flex'
                }}>
                  <Box width={sidePanelOpen && !isTabletSize ? chatContentWidth : '100%'} height={'100%'} style={{ overflow: 'auto' }} position={'absolute'} left={0} id={`message-list-container-${currentConversationSid}`} className="chat-messages-container">
                    {Object.keys(currentConversationMessagesGroupedByDate).map((date, topIndex) => {
                      const _messages = currentConversationMessagesGroupedByDate[date];
                      return (
                        <>
                          {
                            (currentConversationTagsSelected?.length == 0
                              && topIndex == 0
                              && !messagesPaginator[currentConversationSid]) ?
                              <ConversationHeadingMessage
                                as={ChatUiMessage}
                                participantsText={getParticipantsText()} /> : null
                          }
                          {
                            (currentConversationTagsSelected?.length == 0
                              && topIndex == 0
                              && messagesPaginator[currentConversationSid]) ?
                              <LoadPreviousMessages
                                as={ChatUiMessage}
                                onClick={() => {
                                  const messagesScrollContainerEl: any = document.getElementById(`message-list-container-${currentConversationSid}`);
                                  setScrollToValue(messagesScrollContainerEl.scrollHeight);
                                  communicationStore.loadCurrentConversationPreviousPageMessages()
                                }} /> : null
                          }
                          <MessageWrapper as={ChatUiMessage} date={date}>
                            {
                              _messages?.filter((m) => filterByTagsSelected(m, currentConversationTagsSelected))
                                .map((m, i) => {
                                  const isCurrentUser = communicationStore.isCurrentUser(m.author);
                                  const hasMedia = m.attachedMedia?.length > 0;
                                  const emoji = (m.attributes as MessageAttribute)?.emoji as Record<SupportedEmojis, string[]>;
                                  const likes = emoji?.[SupportedEmojis.Likes] ?? [];
                                  const doesCurrentUserLikedTheMessage = communicationStore.doesCurrentUserLikedTheMessage(likes);
                                  const isMessagePinned = communicationStore.isPinned(currentConversationSid, m.sid);
                                  return (
                                    <>
                                      {
                                        (currentConversationTagsSelected?.length == 0
                                          && m.sid == unreadMessageStartSid) ?
                                          <NewMessageIndicator as={ChatUiMessage} count={uiUnreadMessagesCount} /> : null
                                      }
                                      <ChatUiMessage
                                        key={m.sid}
                                        model={{
                                          direction: isCurrentUser
                                            ? 'outgoing'
                                            : 'incoming',
                                          position: 'single',
                                        }}
                                        className={clsx(classes.message, isMessagePinned && classes.pinnedMessage)}
                                        id={`container-${m.sid}`}
                                        data-message-sid={`container-${m.sid}`}
                                      >
                                        {!isCurrentUser && (
                                          <ChatUiAvatar status="available">
                                            <AccountCircleOutlined
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                color: '#777',
                                              }}
                                            />
                                          </ChatUiAvatar>
                                        )}
                                        {!isCurrentUser && (
                                          <CustomMessageHeader
                                            as={ChatUiMessage.Header}
                                            messageSid={m.sid}
                                            name={getAuthorName(m)}
                                            participants={participants[currentConversationSid]}
                                            allowParticipants={internalParticipants}
                                            participantSid={(m.participantSid)}
                                            onHuddle={onHuddle}
                                            onUserNewConversation={onUserNewConversation}
                                            onOpenAddTask={onOpenAddTask}
                                            roles={userIdParticipantDetailsMap}
                                            communicationStore={communicationStore}
                                            loanId={loanId}
                                            chatRoomType={chatRoomType}
                                            ratings={ratings}
                                            calendlyLinks={userIdCalendlyLinkMap}
                                            messageBody={cleanHtmlText(m.body)}
                                            messageCreatedDate={m.dateCreated}
                                            author={m.author}
                                            isPinned={isMessagePinned}
                                          />
                                        )}
                                        {isCurrentUser && isMessagePinned && (
                                          <ChatUiMessage.Header>
                                            <Box display="flex" justifyContent="flex-end" width="100%">
                                              <Chip
                                                icon={<PushPin style={{ color: theme.palette.primary.main, width: 16, height: 16 }} />}
                                                label="Pinned"
                                                style={{
                                                  background: '#fff',
                                                  fontSize: 11,
                                                  height: 22,
                                                  borderRadius: 18,
                                                  border: `1px solid ${theme.palette.primary.main}`,
                                                  color: theme.palette.primary.main,
                                                  fontWeight: 'bold',
                                                }}
                                              />
                                            </Box>
                                          </ChatUiMessage.Header>
                                        )}
                                        <ChatUiMessage.CustomContent>
                                          <>
                                            <MessageHoverMenu firstMessage={i == 0} key={m.sid} message={m} onReplyClick={() => setReplyToMessage(m)} doesCurrentUserLikedTheMessage={doesCurrentUserLikedTheMessage} onLike={() => {
                                              const loggedInParticipantSid = communicationStore.getloggedInParticpant(communicationStore.participants[communicationStore.currentConversationSid])?.sid;
                                              if (loggedInParticipantSid) {
                                                if (doesCurrentUserLikedTheMessage) {
                                                  communicationStore.undoLikeMessage(m, loggedInParticipantSid);
                                                } else {
                                                  communicationStore.likeMessage(m, loggedInParticipantSid);
                                                }
                                              }
                                            }}
                                              isPinned={isMessagePinned}
                                              onPin={() => {
                                                const userId = userStore.userInformation?.userId;
                                                if (communicationStore.isPinned(currentConversationSid, m.sid)) {
                                                  communicationStore.unpinMessage(currentChatRoomId, currentConversationSid, m.sid);
                                                } else {
                                                  communicationStore.pinMessage(currentChatRoomId, currentConversationSid, m, userId);
                                                }
                                              }}>
                                              {hasMedia && (
                                                <>
                                                  {m.attachedMedia.map(media => (
                                                    <AttachmentPreview key={media.sid} media={media} />
                                                  ))}
                                                </>
                                              )}
                                              {
                                                (m.attributes as any)?.replyMessageSid &&
                                                <Box mb={1}>
                                                  <ReplyToMessage
                                                    message={(() => {
                                                      const replyMessageId = (m.attributes as any)?.replyMessageSid;
                                                      const currentTwilioReplyMessages = twilioReplyMessages[currentConversationSid] ?? {};
                                                      const replyToMessage = currentTwilioReplyMessages[replyMessageId] ?? undefined;
                                                      return replyToMessage;
                                                    })()}
                                                    onClick={(replyMessageId) => {
                                                      const messagesContainerEl: any = document.getElementById(`message-list-container-${currentConversationSid}`);
                                                      const replyMessageEl: any = messagesContainerEl.querySelector(`#container-${replyMessageId}`);
                                                      if (replyMessageEl) {
                                                        scrollAndHighlightMessage(messagesContainerEl, replyMessageId);
                                                      }
                                                      else {
                                                        communicationStore.loadCurrentConversationPreviousPageMessagesRecursivelyTillMessageFound(replyMessageId)
                                                          .then(() => {
                                                            setTimeout(() => {
                                                              scrollAndHighlightMessage(messagesContainerEl, replyMessageId);
                                                            }, 1000);
                                                          });
                                                      }
                                                    }}
                                                    style={{
                                                      zoom: 0.8,
                                                      borderRadius: 8,
                                                    }} />
                                                </Box>
                                              }
                                              <HtmlMessageContent id={m.sid} html={m.body} />
                                              {!isNil((m.attributes as MessageAttribute)?.taskId) &&
                                                <span> <a style={{ textDecoration: 'underline', fontWeight: 'bold', color: '#76adb3' }} onClick={() => handleClickHereToOpenReplyInTaskModal((m.attributes as MessageAttribute)?.taskId)}>Click here to open it.</a></span>
                                              }
                                              {likes.length > 0 && (
                                                <LikesChip
                                                  communicationStore={communicationStore}
                                                  likes={likes}
                                                />
                                              )}
                                            </MessageHoverMenu>
                                          </>
                                        </ChatUiMessage.CustomContent>
                                        <ChatUiMessage.Footer
                                          sentTime={getMessageTime(m)}
                                        >
                                          <Box display={'flex'}>
                                            {
                                              isCurrentUser &&
                                              (<Box marginTop={'-3px'}>
                                                <ReadReceiptButton communicationStore={communicationStore} message={m} />
                                              </Box>)
                                            }
                                            <Box
                                              borderRadius={8}
                                              fontSize={10}
                                              color={'#777'}
                                              fontWeight={'bold'}
                                            >
                                              {isChatRoomActive() && (
                                                <ReplyButton onClick={() => setReplyToMessage(m)} />
                                              )}
                                              {!isCurrentUser && isChatRoomActive() && isUWChatRoom && isInternalUser && (
                                                <ReplyInTaskButton onClick={() => handleOpenReplyInTaskModal(m)} />
                                              )}
                                            </Box>
                                            <Box className="cs-message__sent-time" style={{ marginLeft: '8px', paddingLeft: '0px' }}>
                                              {getMessageTime(m)}
                                            </Box>
                                          </Box>
                                        </ChatUiMessage.Footer>
                                      </ChatUiMessage>
                                    </>
                                  );
                                })
                            }
                          </MessageWrapper>
                        </>
                      );
                    })
                    }

                  </Box>
                  {!isTabletSize && isInternalUser && showAiSummary &&
                    <Box width={sidePanelWidth} height={'100%'} style={{ overflow: 'auto', borderLeft: '1px solid #ccc' }} position={'absolute'} right={0} className="conversation-summary">
                      <ConversationSummary handleClose={() => setShowAiSummary(false)} communicationStore={communicationStore} />
                    </Box>}
                  {!isTabletSize && isInternalUser && bestPractices &&
                    <Box width={sidePanelWidth} height={'100%'} style={{ overflow: 'auto', borderLeft: '1px solid #ccc' }} position={'absolute'} right={0} className="best-practices">
                      <BestPractices handleClose={() => setBestPractices(false)} />
                    </Box>}
                  {!isTabletSize && showPinnedMessages &&
                    <Box width={sidePanelWidth} height={'100%'} style={{ overflowY: 'auto', overflowX: 'hidden', borderLeft: '1px solid #ccc' }} position={'absolute'} right={0} className="pinned-messages">
                      <PinnedMessages
                        handleClose={() => setShowPinnedMessages(false)}
                        communicationStore={communicationStore}
                        onScrollToMessage={(messageSid) => {
                          const messagesContainerEl = document.getElementById(`message-list-container-${currentConversationSid}`);
                          const el = messagesContainerEl?.querySelector(`#container-${messageSid}`);
                          if (el) {
                            scrollAndHighlightMessage(messagesContainerEl, messageSid);
                          } else {
                            communicationStore.loadCurrentConversationPreviousPageMessagesRecursivelyTillMessageFound(messageSid)
                              .then(() => {
                                setTimeout(() => scrollAndHighlightMessage(messagesContainerEl, messageSid), 1000);
                              });
                          }
                        }}
                      />
                    </Box>}
                  {isTabletSize &&
                    <Drawer open={isInternalUser && showAiSummary} variant={'temporary'} anchor={'right'} onClose={() => setShowAiSummary(false)}
                      ModalProps={{ keepMounted: true }} PaperProps={{
                        style: {
                          width: '80%'
                        }
                      }}>
                      <ConversationSummary handleClose={() => setShowAiSummary(false)} communicationStore={communicationStore} />
                    </Drawer>}
                  {isTabletSize &&
                    <Drawer open={isInternalUser && bestPractices} variant={'temporary'} anchor={'right'} onClose={() => setBestPractices(false)}
                      ModalProps={{ keepMounted: true }} PaperProps={{
                        style: {
                          width: '80%'
                        }
                      }}>
                      <BestPractices handleClose={() => setBestPractices(false)} />
                    </Drawer>}
                  {isTabletSize &&
                    <Drawer open={showPinnedMessages} variant={'temporary'} anchor={'right'} onClose={() => setShowPinnedMessages(false)}
                      ModalProps={{ keepMounted: true }} PaperProps={{
                        style: {
                          width: '80%'
                        }
                      }}>
                      <PinnedMessages
                        handleClose={() => setShowPinnedMessages(false)}
                        communicationStore={communicationStore}
                        onScrollToMessage={(messageSid) => {
                          const messagesContainerEl = document.getElementById(`message-list-container-${currentConversationSid}`);
                          const el = messagesContainerEl?.querySelector(`#container-${messageSid}`);
                          if (el) {
                            scrollAndHighlightMessage(messagesContainerEl, messageSid);
                          } else {
                            communicationStore.loadCurrentConversationPreviousPageMessagesRecursivelyTillMessageFound(messageSid)
                              .then(() => {
                                setTimeout(() => scrollAndHighlightMessage(messagesContainerEl, messageSid), 1000);
                              });
                          }
                        }}
                      />
                    </Drawer>}
                </MessageList.Content>
              }
              {
                !isConversationOpen && !loadingCurrentConversation && showNoConversationsMessage ?
                  <MessageList.Content className={classes.emptyContentMessageList}>
                    No Conversations.
                  </MessageList.Content> : null
              }
              {
                !isConversationOpen && !loadingCurrentConversation && !showNoConversationsMessage &&
                (
                  !(communicationStore instanceof TechSupportCommunicationStore)
                    ? <MessageList.Content className={classes.emptyContentMessageList}>
                      {
                        showLoading ? <CircularLoader position="absolute" thickness={5} color="primary" size={36} /> :
                          'Select a conversation on the left to get started.'
                      }
                    </MessageList.Content>
                    : communicationStore instanceof TechSupportCommunicationStore
                      ? <MessageList.Content className={classes.emptyContentMessageList}>
                        Loading...
                      </MessageList.Content>
                      : null
                )
              }
              {
                isConversationOpen && !loadingCurrentConversation && noMessages ?
                  <MessageList.Content style={{ display: 'flex' }}>
                    <Box width={sidePanelOpen && !isTabletSize ? chatContentWidth : '100%'} height={'100%'} position={'absolute'} left={0}
                      className={classes.emptyContentMessageList}>
                      No Messages.
                    </Box>
                    {!isTabletSize && isInternalUser && showAiSummary &&
                      <Box width={sidePanelWidth} style={{ overflow: 'auto', borderLeft: '1px solid #ccc' }} height={'100%'} position={'absolute'} right={0} className="conversation-summary">
                        <ConversationSummary handleClose={() => setShowAiSummary(false)} communicationStore={communicationStore} />
                      </Box>}
                    {!isTabletSize && isInternalUser && bestPractices &&
                      <Box width={sidePanelWidth} style={{ overflow: 'auto', borderLeft: '1px solid #ccc' }} height={'100%'} position={'absolute'} right={0} className="best-practices">
                        <BestPractices handleClose={() => setBestPractices(false)} />
                      </Box>}
                    {!isTabletSize && showPinnedMessages &&
                      <Box width={sidePanelWidth} style={{ overflowY: 'auto', overflowX: 'hidden', borderLeft: '1px solid #ccc' }} height={'100%'} position={'absolute'} right={0} className="pinned-messages">
                        <PinnedMessages
                          handleClose={() => setShowPinnedMessages(false)}
                          communicationStore={communicationStore}
                          onScrollToMessage={(messageSid) => {
                            const messagesContainerEl = document.getElementById(`message-list-container-${currentConversationSid}`);
                            const el = messagesContainerEl?.querySelector(`#container-${messageSid}`);
                            if (el) {
                              scrollAndHighlightMessage(messagesContainerEl, messageSid);
                            } else {
                              communicationStore.loadCurrentConversationPreviousPageMessagesRecursivelyTillMessageFound(messageSid)
                                .then(() => {
                                  setTimeout(() => scrollAndHighlightMessage(messagesContainerEl, messageSid), 1000);
                                });
                            }
                          }}
                        />
                      </Box>}
                    {isTabletSize &&
                      <Drawer open={isInternalUser && showAiSummary} variant={'temporary'} anchor={'right'} onClose={() => setShowAiSummary(false)}
                        ModalProps={{ keepMounted: true }} PaperProps={{
                          style: {
                            width: '80%'
                          }
                        }}>
                        <ConversationSummary handleClose={() => setShowAiSummary(false)} communicationStore={communicationStore} />
                      </Drawer>}
                    {isTabletSize &&
                      <Drawer open={isInternalUser && bestPractices} variant={'temporary'} anchor={'right'} onClose={() => setBestPractices(false)}
                        ModalProps={{ keepMounted: true }} PaperProps={{
                          style: {
                            width: '80%'
                          }
                        }}>
                        <BestPractices handleClose={() => setBestPractices(false)} />
                      </Drawer>}
                    {isTabletSize &&
                      <Drawer open={showPinnedMessages} variant={'temporary'} anchor={'right'} onClose={() => setShowPinnedMessages(false)}
                        ModalProps={{ keepMounted: true }} PaperProps={{
                          style: {
                            width: '80%'
                          }
                        }}>
                        <PinnedMessages
                          handleClose={() => setShowPinnedMessages(false)}
                          communicationStore={communicationStore}
                          onScrollToMessage={(messageSid) => {
                            const messagesContainerEl = document.getElementById(`message-list-container-${currentConversationSid}`);
                            const el = messagesContainerEl?.querySelector(`#container-${messageSid}`);
                            if (el) {
                              scrollAndHighlightMessage(messagesContainerEl, messageSid);
                            } else {
                              communicationStore.loadCurrentConversationPreviousPageMessagesRecursivelyTillMessageFound(messageSid)
                                .then(() => {
                                  setTimeout(() => scrollAndHighlightMessage(messagesContainerEl, messageSid), 1000);
                                });
                            }
                          }}
                        />
                      </Drawer>}
                  </MessageList.Content> : null
              }
            </MessageList>
        }
        {
          isConversationOpen && !currentConversationReadOnly && !currentConversationFullReadOnly && isChatRoomActive() ?
            <CustomMessageInputContainer
              as={MessageInput}
              roles={userIdParticipantDetailsMap}
              chatRoomType={chatRoomType}
              replyToMessage={replyToMessage}
              onReplyClear={() => setReplyToMessage(null)}
              sendMessage={onMessageSend}
              communicationStore={communicationStore}
              mentionsList={mentionsList}
              onChange={() => {
                openedConversation.typing();
              }}
              messageSnippets={communicationStore.messageSnippets}
              expandMessageInputArea={expandMessageInputArea}
              toggleExpand={toggleExpand}
              loading={communicationStore.loading}
              showSnippet={showSnippet}
              participants={participants}
              currentConversationSid={currentConversationSid}
              isTabletSize={isTabletSize} /> : null

        }
        {
          isConversationOpen && currentConversationReadOnly && allowJoinConversation ?
            <JoinToolBox
              as={InputToolbox}
              communicationStore={communicationStore} /> : null
        }
        {
          isConversationOpen && currentConversationIsArchived ?
            <ArchivedChatInfoBox as={InputToolbox} communicationStore={communicationStore} /> : null
        }

        {
          isConversationOpen && currentConversationFullReadOnly ?
            <FullReadOnlyChatInfoBox as={InputToolbox} communicationStore={communicationStore} /> : null
        }
      </ChatContainer>
      <ManageParticipantsDialog
        communicationStore={communicationStore}
        allowAddParticipants={allowAddParticipants}
        allowAddInternalParticipants={allowAddInternalParticipants}
        allowRemoveParticipants={allowRemoveParticipants}
        showEmailAddress={showEmailAddress}
        loanId={loanId}
        taskId={taskId}
      />
      <ConfirmDialog
        open={showEscalateTaskModal}
        handleConfirm={escalateTask}
        handleClose={() => setShowEscalateTaskModal(false)}
        handleCancel={() => setShowEscalateTaskModal(false)}
        body={<Typography>Please confirm you want to escalate this task.</Typography>}
        confirmButtonText="Confirm"
        cancelButtonText="Close"
        title={'Confirmation'}
      />
    </>
  )
});

const CustomConversationHeaderContent = observer((
  {
    loanId,
    chatRoomType,
    displayName,
    communicationStore,
    hasParticipantsFetched,
  }:
    {
      loanId: number;
      chatRoomType: ChatRoomType;
      displayName: string;
      communicationStore: CommunicationStore;
      hasParticipantsFetched: boolean;
    }) => {
  const [state, setState] = useState<"NORMAL" | "EDIT">("NORMAL");
  const [isBorrowerRoom, setIsBorrowerRoom] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [newDisplayName, setNewDisplayName] = useState<string>('');
  const isBorrowerUser = communicationStore.globalStore.isBorrowerUser;

  useEffect(() => {
    if (hasParticipantsFetched) {
      setIsBorrowerRoom(communicationStore.isBorrowerRoom(communicationStore.currentConversation));
    }
  }, [hasParticipantsFetched]);

  useEffect(() => {
    setName(displayName);
  }, [displayName]);
  return (
    <div className="cs-conversation-header__user-name" style={{
      display: 'flex',
      alignItems: 'center',
    }}>
      <Tooltip title={name} placement="top" arrow>
        <Box minWidth={0} overflow={'hidden'} textOverflow={'ellipsis'} maxWidth={"100%"}>
          {
            state == "EDIT" ?
              <TextField style={{ marginRight: '20px', height: 24 }}
                autoFocus
                testId="conversationName"
                size='small'
                variant="outlined"
                label=""
                type="email"
                value={newDisplayName}
                onChange={e => setNewDisplayName(e.target.value)}
                inputProps={{
                  style: {
                    padding: '2px 8px'
                  },
                }}
                InputProps={{
                  style: {
                    marginTop: '2px'
                  },
                }}
                fullWidth
              />
              : <>
                {
                  isBorrowerRoom && !isBorrowerUser && <BorrowerIndicatorIcon />
                }
                {name}
              </>
          }
        </Box>
      </Tooltip>
      {
        chatRoomType == ChatRoomType.ADHOC &&
        <Box>
          {
            state == "EDIT" ?
              <>
                <Tooltip title="Save" placement="top" arrow>
                  <IconButton color="primary" onClick={() => {
                    if (newDisplayName && newDisplayName.trim()) {
                      if (newDisplayName != name) {
                        communicationStore.updateConversationName(newDisplayName, communicationStore.currentChatRoomId, loanId)
                          .then((result) => {
                            if (result) {
                              setName(newDisplayName);
                            }
                          })
                          .finally(() => {
                            setState("NORMAL");
                          });
                      } else {
                        setState("NORMAL");
                      }
                    }
                  }} size="small">

                    <Save fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel" placement="top" arrow>
                  <IconButton color="default" onClick={() => {
                    setNewDisplayName('');
                    setState("NORMAL");
                  }} size="small">
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
              :
              <Tooltip title="Edit" placement="top" arrow>
                <IconButton color="primary" onClick={() => {
                  setState("EDIT");
                  setNewDisplayName(displayName);
                }}
                  size="small"
                  style={{
                    padding: 2,
                    marginLeft: 4,
                    width: 24,
                    height: 24,
                  }}>
                  <Edit style={{
                    fontSize: 16
                  }} />
                </IconButton>
              </Tooltip>
          }
        </Box>
      }
    </div>
  )
});

const UserRating = ({ messageSid, senderId, receiverId, loanId, chatRoomId, ratings, messageBody, messageCreatedDate, setHaveRating, chatRoomName }) => {
  const { userStore } = useUserStore();
  const [commentBoxOpen, setCommentBoxOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [temporalRating, setTemporalRating] = useState(0);
  const [comment, setComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  const handleRatingClick = (newRating) => {
    setTemporalRating(newRating);
    setCommentBoxOpen(true);
  };

  const handleCommentSubmit = async () => {
    setRating(temporalRating);
    const userRatingRequest = {
      loanId: loanId,
      chatRoomId: chatRoomId,
      chatRoomName: chatRoomName,
      senderUserId: senderId,
      receiverUserId: receiverId,
      comment: comment,
      rating: temporalRating,
      messageBody: messageBody,
      messageCreatedDate: messageCreatedDate,
    };
    await userStore.submitUserRating(userRatingRequest);
    await userStore.fetchAllRatingsByChatRoomIdAndUserId(chatRoomId, senderId);
    setCommentBoxOpen(false);
    setComment('');
    setRatingSubmitted(true);
    setHaveRating(true)
  };

  const closeCommentDialog = () => {
    setRating(0);
    setTemporalRating(0);
    setCommentBoxOpen(false);
    setComment('');
  };

  useEffect(() => {
    const existingRating = ratings ? ratings.find(r => r.receiverUserId === receiverId) : false;
    if (existingRating) {
      setRating(existingRating.rating);
      setComment(existingRating.comment);
      setHaveRating(true);
      setRatingSubmitted(true);
    }
  }, [ratings, receiverId]);

  return (
    <>

      <Typography style={{
        fontWeight: 'bold',
        color: 'black',
        fontSize: 15
      }}>
        <Box display="flex" alignItems="center">
          <span>How am I doing? Rate me anonymously:</span>
          <Rating
            name={messageSid}
            value={rating}
            onChange={(event, newValue) => {
              handleRatingClick(newValue);
            }}
            disabled={ratingSubmitted}
            precision={1}
            size="medium"
          />
        </Box>
      </Typography>


      <Dialog open={commentBoxOpen} onClose={() => setCommentBoxOpen(false)} fullWidth={true} maxWidth="sm">
        <DialogTitle>How am I doing?</DialogTitle>
        <DialogContent>
          <TextField
            testId="comment"
            standaloneLabel
            variant="outlined"
            value={comment}
            onChange={e => setComment(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCommentDialog} color="primary" variant="outlined" testId="cancel">
            Cancel
          </Button>
          <Button onClick={handleCommentSubmit} color="primary" variant="contained" testId="submit">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CustomMessageHeader = observer(({ as, messageSid, name, participants, allowParticipants, participantSid, onHuddle, onUserNewConversation, onOpenAddTask, roles, communicationStore, loanId, chatRoomType, ratings, calendlyLinks, messageBody, messageCreatedDate, author, isPinned }: {
  as: any,
  messageSid: string,
  name: string,
  participants: Participant[],
  allowParticipants: Record<number, ParticipantDetails>,
  participantSid: string,
  onHuddle: (inviteUserIds: number[]) => void,
  onUserNewConversation: (inviteUserId: number) => void,
  onOpenAddTask: (userId: number) => void,
  roles: Record<number, ParticipantDetails>,
  communicationStore: CommunicationStore,
  loanId: number,
  chatRoomType: ChatRoomType,
  ratings: any;
  calendlyLinks: Record<number, string>,
  messageBody: string,
  messageCreatedDate: Date,
  author: string,
  isPinned?: boolean,
}) => {
  let isInternalUser = null;
  let roleName = null;
  let hasRole = null;
  let roleColor = null;
  let isActive = true;
  let companyName = null;
  const theme = useTheme();
  const userFromAllowedParticipants = author ? getUserFromAllowedParticipants(Number(author.split('_')[0]), allowParticipants) : undefined;
  const participant = participants?.find(p => {
    if (p.sid == participantSid) {
      return true;
    } else {
      return false;
    }
  });
  const userId = participant ? (participant.attributes as ParticipantAttribute).userId : userFromAllowedParticipants;
  const hideHuddle = (participant?.attributes as ParticipantAttribute)?.isSystemUser ?? true;
  const [haveRating, setHaveRating] = useState();
  if (ratings) {
    checkIfExistingRating(ratings, userId)
  }

  if (participant) {
    isInternalUser = userId !== undefined ? isInternal(userId.toString(), roles) : null;

    if (userId !== undefined) {
      const roleInfo = getRoleInfoForAuthor(userId.toString(), roles);

      roleName = roleInfo?.role ?? null;
      hasRole = roleInfo?.hasRole ?? null;
      roleColor = roleInfo?.roleColor ?? null;
      isActive = roleInfo?.isActive ?? true;
      companyName = roleInfo?.companyName ?? null;
    }
  } else {
    isInternalUser = (userId !== undefined && userId !== null) ? isInternal(userId.toString(), allowParticipants) : null;

    if (userId !== undefined && userId !== null) {
      const roleInfo = getRoleInfoForAuthor(userId.toString(), allowParticipants);

      roleName = roleInfo?.role ?? null;
      hasRole = roleInfo?.hasRole ?? null;
      roleColor = roleInfo?.roleColor ?? null;
      isActive = roleInfo?.isActive ?? true;
      companyName = roleInfo?.companyName ?? null;
    }
  }
  const loggedInUser = communicationStore.getloggedInParticpant(communicationStore.participants[communicationStore.currentConversationSid])?.attributes as ParticipantAttribute;
  const isNportalUser = loggedInUser?.userId !== undefined ? isInternal(loggedInUser.userId.toString(), roles) : null;
  const participantCalendlyLink = calendlyLinks[userId] || null;
  const chipLabel = hasRole ? roleName : (!isInternalUser ? companyName : null);

  return (
    <Box display={'flex'} alignItems='center' bgcolor={(!haveRating && chatRoomType === ChatRoomType.UNDERWRITING && !isNportalUser && isInternalUser) ? '#fff9c4' : ''}>
      <Box mr={1}>
        <Typography style={{
          fontWeight: 'bold',
          color: '#666',
          fontSize: 14
        }}>
          <Box display="flex" alignItems="center" gridGap="5px">
            <DisplayNameWithOnlineStatus
              userId={userId}
              displayName={name}
              isActive={isActive}
              allowHuddle={!hideHuddle}
              onHuddle={onHuddle}
              onUserNewConversation={onUserNewConversation}
              onOpenAddTask={onOpenAddTask}
              showHoverPopup={true}
              participantCalendlyLink={participantCalendlyLink}
              roleName={roleName}
              isInternalUser={isInternalUser} />
            {isActive && chipLabel &&
              <Chip variant="default"
                size="small"
                label={chipLabel}
                style={{
                  height: 18,
                  margin: 4,
                  backgroundColor: roleColor,
                  color: roleColor ? theme.palette.getContrastText(roleColor) : 'black',
                  fontWeight: 'bold'
                }} />
            }
            {isPinned && (
              <Chip
                icon={<PushPin style={{ color: theme.palette.primary.main, width: 16, height: 16 }} />}
                label="Pinned"
                style={{
                  background: '#fff',
                  fontSize: 11,
                  height: 22,
                  borderRadius: 18,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                  margin: 4,
                }}
              />
            )}
            {userId != undefined && isInternalUser && !isNportalUser &&
              <UserRating
                key={userId}
                messageSid={messageSid}
                senderId={loggedInUser?.userId}
                receiverId={userId}
                loanId={loanId}
                chatRoomId={communicationStore.currentChatRoomId}
                chatRoomName={communicationStore.currentChatRoomName}
                ratings={ratings}
                messageBody={messageBody}
                messageCreatedDate={messageCreatedDate}
                setHaveRating={setHaveRating}
              />
            }
          </Box>
        </Typography>
      </Box>
    </Box>
  );
});

const ConversationHeadingMessage = observer(({ as, participantsText }: {
  as: any,
  participantsText: any,
}) => {
  return (
    <Box pt={3} px={1} mx={2} fontSize={20} color={'#a5a5a5'}>
      <Typography variant={'h6'}>
        This is the very beginning of the conversation.
      </Typography>
      <Typography variant={'h6'}>
        <span>This conversation has </span>
        {participantsText}
        <span>.</span>
      </Typography>
    </Box>
  );
});

const LoadPreviousMessages = observer(({ as, onClick }: {
  as: any,
  onClick: any
}) => {
  return (
    <Box width="100%"
      display={'flex'}
      justifyContent={'center'}>
      <Button testId="loanPreviousMessages" variant="text" color="secondary" onClick={onClick} style={{
        marginTop: 8,
        textDecoration: 'underline',
        textTransform: 'capitalize',
        letterSpacing: 'normal',
      }}>
        Load Previous Messages
      </Button>
    </Box>
  );
});

const NewMessageIndicator = observer(({ as, count }: {
  as: any,
  count: number
}) => {
  return (
    <Box position="relative" width="100%">
      <Box position="absolute" width="100%" display={'flex'} fontSize={12} fontWeight="bold" color={"#ff0000"}>
        <Box flexGrow={1} alignItems='center' display={'flex'}>
          <Box width={'100%'} height={'1px'} bgcolor={'#ff0000'}>
          </Box>
        </Box>
        <Box mx={2}>
          New
        </Box>
        <Box flexGrow={1} alignItems='center' display={'flex'}>
          <Box width={'100%'} height={'1px'} bgcolor={'#ff0000'}>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

const MessageWrapper = observer(({ as, date, children }: {
  as: any,
  date: any,
  children: any,
}) => {
  return (
    <>
      {children}
    </>
  );
  /* const _date = new Date(date);
  const formattedDate = isToday(_date) ? 'Today' : format(_date, 'EEEE, MMMM do');
  return (
    <Box position="relative" width="100%" my={2}>
      <Box position="absolute" top={24} width="100%" display={'flex'}>
        <Box flexGrow={1} alignItems='center' display={'flex'}>
          <Box width={'100%'} height={'1px'} bgcolor={'#175689'}>
          </Box>
        </Box>
      </Box>
      <Box position="sticky" top={0} width="100%" display={'flex'} justifyContent={'center'} zIndex={1}>
        <Box mt={1}>
          <Chip
            variant="outlined"
            label={formattedDate}
            style={{
              color: '#175689',
              borderColor: '#175689',
              background: '#fff',
              fontWeight: 'bold'
            }}
          />
        </Box>
      </Box>
      {children}
    </Box>
  );*/
});
